import { z } from "zod";
import { INQUIRY_STATUS } from "../enums.js";
import { BOOKING_STATUS } from "./booking.js";

// ============================================================================
// Unified lead inbox — normalizes the 4 lead sources (inquiries, bookings,
// seller_leads, valuation_requests) into one stream + a shared 3-stage pipeline.
// Each source keeps its NATIVE status; `stage` is the normalized rollup.
// ============================================================================

export const LEAD_SOURCES = ["inquiry", "booking", "seller", "valuation"] as const;
export type LeadSource = (typeof LEAD_SOURCES)[number];

export const LEAD_SOURCE_LABEL: Record<LeadSource, string> = {
  inquiry: "Richiesta",
  booking: "Visita",
  seller: "Vendi casa",
  valuation: "Valutazione",
};

export const LEAD_STAGES = ["nuovo", "in_corso", "chiuso"] as const;
export type LeadStage = (typeof LEAD_STAGES)[number];
export const LEAD_STAGE_LABEL: Record<LeadStage, string> = {
  nuovo: "Nuovi",
  in_corso: "In corso",
  chiuso: "Chiusi",
};

/** Native status -> normalized pipeline stage. */
export function normalizeStage(source: LeadSource, status: string): LeadStage {
  if (source === "booking") {
    if (status === "richiesta") return "nuovo";
    if (status === "confermata") return "in_corso";
    return "chiuso"; // completata | annullata
  }
  // inquiry | seller | valuation use inquiry_status
  if (status === "nuovo") return "nuovo";
  if (status === "chiuso") return "chiuso";
  return "in_corso"; // visto | in_carico
}

/** The native status values a given source can be set to (drawer controls). */
export function statusOptionsFor(source: LeadSource): readonly string[] {
  return source === "booking" ? BOOKING_STATUS : INQUIRY_STATUS;
}

/** Validate a status value belongs to the source's native vocabulary. */
export function isValidStatusFor(source: LeadSource, status: string): boolean {
  return statusOptionsFor(source).includes(status as never);
}

export const UnifiedLead = z.object({
  source: z.enum(LEAD_SOURCES),
  id: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  subject: z.string(),
  message: z.string().nullable(),
  status: z.string(),
  stage: z.enum(LEAD_STAGES),
  listing_id: z.string().nullable(),
  listing_slug: z.string().nullable(),
  listing_title: z.string().nullable(),
  created_at: z.string(),
  meta: z.record(z.unknown()),
});
export type UnifiedLead = z.infer<typeof UnifiedLead>;

export const InboxResponse = z.object({
  items: z.array(UnifiedLead),
  total: z.number(),
  // True when any source hit the per-source fetch cap (counts/list reflect the
  // most recent N per source, not the full history).
  truncated: z.boolean(),
  counts: z.object({
    stage: z.object({ nuovo: z.number(), in_corso: z.number(), chiuso: z.number() }),
    source: z.record(z.number()),
  }),
});
export type InboxResponse = z.infer<typeof InboxResponse>;

export const LeadStatusPatchInput = z.object({ status: z.string().min(1).max(40) });
export type LeadStatusPatchInput = z.infer<typeof LeadStatusPatchInput>;
